import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { InvoiceService } from '../../../shared/services/invoice.service';
import { ToastyService } from 'ng2-toasty';
import { DataTable, DataTableTranslations } from 'angular-datatables-all-versions';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { fromEvent } from 'rxjs/observable/fromEvent';

import { InvoiceListDataSource } from './invoice-list.datasource';
import { IPaginationInvoices, Invoice } from '../../../shared/models';

@Component({
  selector: 'app-invoice-list',
  templateUrl: './invoice-list.component.html',
  styleUrls: ['./invoice-list.component.css']
})
export class InvoiceListComponent implements OnInit, AfterViewInit {

  @ViewChild('search_input', { static: true }) searchInputRef: ElementRef;
  @ViewChild('search_btn', { static: true }) searchBtnRef: ElementRef;
  @ViewChild(DataTable, { static: true }) invoiceTable: DataTable;

  invoices: Invoice[] = [];
  invoiceCount = 0;
  offset = 0;
  limits = [25, 50, 100];
  invoiceType: string;

  // Filtres
  selectedMonth: string = '';
  selectedYear: string = '';
  months = [
    { value: '1', label: 'Janvier' },
    { value: '2', label: 'Février' },
    { value: '3', label: 'Mars' },
    { value: '4', label: 'Avril' },
    { value: '5', label: 'Mai' },
    { value: '6', label: 'Juin' },
    { value: '7', label: 'Juillet' },
    { value: '8', label: 'Août' },
    { value: '9', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' }
  ];
  years: number[] = [];

  dataSource: InvoiceListDataSource;

  translations = <DataTableTranslations>{
    indexColumn: 'Num.',
    expandColumn: 'Informations',
    selectColumn: 'Sélectionner',
    paginationLimit: 'Max.',
    paginationText: 'Résultats: {from} - {to} / {total}',
    paginationTotalPages: '/'
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastyService: ToastyService,
    private invoiceService: InvoiceService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.dataSource = new InvoiceListDataSource(invoiceService);
  }

  ngOnInit() {
    // Initialiser les années (5 dernières années)
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 5; i++) {
      this.years.push(currentYear - i);
    }

    this.route.params.subscribe(params => {
      this.invoiceType = params['type'] || 'solidaire';
      const invoiceTypeNumeric = this.getInvoiceTypeNumeric(this.invoiceType);
      this.dataSource.load(invoiceTypeNumeric, '', 1, 50, null, this.selectedMonth, this.selectedYear);
      this.dataSource.pagination.subscribe(
        (data: IPaginationInvoices) => {
          if (data && data.hasOwnProperty('pagination')) {
            this.invoiceCount = data.pagination.total;
            this.invoices = data.data;
          }
        });
    });
  }

  ngAfterViewInit(): void {
    fromEvent(this.searchBtnRef.nativeElement, 'click').pipe(
      debounceTime(100),
      distinctUntilChanged(),
      tap(() => {
        this.changeDetector.detectChanges();
        this.invoiceTable.offset = 1;
        this.loadInvoicesPage();
      })
    ).subscribe(() => {
      this.dataSource.pagination.subscribe(() => {
        this.changeDetector.detectChanges();
      });
    });
  }

  reload(params) {
    // To prevent internal error when the current page number < 1
    if (this.invoiceTable.page < 1) {
      this.offset = 1;
    }
    this.loadInvoicesPage(params);
  }

  private loadInvoicesPage(sort = null) {
    this.offset = this.invoiceTable.page < 1 ? 1 : this.invoiceTable.page;
    const invoiceTypeNumeric = this.getInvoiceTypeNumeric(this.invoiceType);
    this.dataSource.load(
      invoiceTypeNumeric,
      this.searchInputRef.nativeElement.value,
      this.offset,
      this.invoiceTable.limit,
      sort,
      this.selectedMonth,
      this.selectedYear
    );
  }

  /**
   * Télécharge le PDF d'une facture
   */
  downloadInvoice(invoice: Invoice) {
    this.invoiceService.downloadInvoicePdf(invoice.invoiceNumber).subscribe(
      (result) => {
        if (result.success) {
          this.toastyService.success({
            title: 'Téléchargement réussi',
            msg: 'Le PDF a été téléchargé avec succès',
            theme: 'bootstrap',
            showClose: true,
            timeout: 5000
          });
        } else {
          this.toastyService.error({
            title: 'Erreur',
            msg: result.error || 'Erreur lors du téléchargement du PDF',
            theme: 'bootstrap',
            showClose: true,
            timeout: 5000
          });
        }
      },
      (error) => {
        this.toastyService.error({
          title: 'Erreur',
          msg: 'Erreur lors du téléchargement du PDF',
          theme: 'bootstrap',
          showClose: true,
          timeout: 5000
        });
      }
    );
  }

  /**
   * Formate le montant en centimes en euros
   */
  formatAmount(amountInCents: number, convertInHt: boolean = false): string {
    let amount = amountInCents;
    if (convertInHt) {
      // Convertir le montant TTC en HT (en divisant par 1.2)
      amount = amountInCents / 1.2;
    }
    
    
    return (amount / 100).toFixed(2);
  }

  /**
   * Retourne le titre de la page selon le type de facture
   */
  getPageTitle(): string {
    if (this.invoiceType === 'impact') {
      return 'Factures - Students for Impact';
    }
    if (this.invoiceType === 'citizens') {
      return 'Factures - Citizens for Impact';
    }
    return 'Factures - Cagnotte Solidaire';
  }

  /**
   * Convertit le type de facture en numérique pour l'API
   * solidaire = 2, impact = 1, citizens = 4
   */
  private getInvoiceTypeNumeric(type: string): string {
    if (type === 'impact') {
      return '1';
    }
    if (type === 'citizens') {
      return '4';
    }
    return '2';
  }

  /**
   * Applique les filtres mois/année
   */
  onFilterChange() {
    this.invoiceTable.offset = 1;
    this.offset = 1;
    this.loadInvoicesPage();
  }
}
